export const CBE_OOTY_PATH = '/coimbatore-to-ooty';
export const CBE_OOTY_REVERSE_PATH = '/ooty-to-coimbatore';
export const CBE_OOTY_ORIGIN = 'Coimbatore';
export const CBE_OOTY_DESTINATION = 'Ooty';
export const CBE_OOTY_AIRPORT_PICKUP = 'Coimbatore Airport';

export const CBE_OOTY_SEO = {
  title: 'Coimbatore to Ooty Taxi Service | One Way Cab | EasyDropTaxi',
  description:
    'Book a comfortable Coimbatore to Ooty taxi with EasyDropTaxi. Editable pickup and drop, transparent fare estimates from live mapped distance, and Sedan, SUV or Crysta options.',
  image: '/images/destinations/ooty/hero-nilgiris.webp',
  h1: 'Coimbatore to Ooty Taxi',
  heroLead:
    'Ride from Coimbatore into the Nilgiris with a private taxi, then add a local Ooty sightseeing cab if you want the gardens, lakes, and viewpoints. Taxi fares and sightseeing plans are quoted separately.',
};

/** Published sightseeing-cab fares are reduced by ₹100. Taxi fares are not. */
const fromReferenceFare = (referencePrice) => referencePrice - 100;

/**
 * One photograph per place. Files are Wikimedia Commons originals converted
 * to WebP. CC BY and CC BY-SA require the credit line rendered on the page.
 */
export const OOTY_DESTINATION_IMAGES = {
  hero: {
    src: '/images/destinations/ooty/hero-nilgiris.webp',
    alt: 'Mist over tea plantations in the Nilgiri hills',
    author: 'N. Vivekananthamoorthy',
    license: 'CC BY 4.0',
    licenseHref: 'https://creativecommons.org/licenses/by/4.0/',
    sourceHref: 'https://commons.wikimedia.org/wiki/File:Nilgiris-Tea-Plantations-Tamil-Nadu-India.jpg',
  },
  botanical: {
    src: '/images/destinations/ooty/botanical-garden.webp',
    alt: 'Pavilion and planted beds at the Government Botanical Garden, Ooty',
    author: 'Rangan Datta Wiki',
    license: 'CC BY-SA 4.0',
    licenseHref: 'https://creativecommons.org/licenses/by-sa/4.0/',
    sourceHref: 'https://commons.wikimedia.org/wiki/File:A_pavilion,_Botanical_Garden,_Ooty.jpg',
  },
  rose: {
    src: '/images/destinations/ooty/rose-garden.webp',
    alt: 'Terraced beds at the Government Rose Garden, Ooty',
    author: 'Rangan Datta Wiki',
    license: 'CC BY-SA 4.0',
    licenseHref: 'https://creativecommons.org/licenses/by-sa/4.0/',
    sourceHref: 'https://commons.wikimedia.org/wiki/File:Rose_Garden,_Ooty_4.jpg',
  },
  doddabetta: {
    src: '/images/destinations/ooty/doddabetta.webp',
    alt: 'Forested Nilgiri ridges seen from Doddabetta Peak',
    author: 'Baswameenakshi',
    license: 'CC0',
    licenseHref: 'https://creativecommons.org/publicdomain/zero/1.0/',
    sourceHref: 'https://commons.wikimedia.org/wiki/File:Doddabetta-Peak.jpg',
  },
  lake: {
    src: '/images/destinations/ooty/ooty-lake.webp',
    alt: 'Boats on Ooty Lake, with trees along the shore',
    author: 'Thavasvi arunachalam',
    license: 'CC BY-SA 4.0',
    licenseHref: 'https://creativecommons.org/licenses/by-sa/4.0/',
    sourceHref: 'https://commons.wikimedia.org/wiki/File:Lake_in_ooty.jpg',
  },
  pykara: {
    src: '/images/destinations/ooty/pykara.webp',
    alt: 'Rocky pools and small cascades at Pykara Falls, near Ooty',
    author: 'Shanmugamp7',
    license: 'CC BY-SA 4.0',
    licenseHref: 'https://creativecommons.org/licenses/by-sa/4.0/',
    sourceHref: 'https://commons.wikimedia.org/wiki/File:Pykara_falls,_Ooty_-_1.jpg',
  },
  pine: {
    src: '/images/destinations/ooty/pine-forest.webp',
    alt: 'Tall pine trunks in the Pine Forest, Ooty',
    author: 'Vinodkumar Kotabagi',
    license: 'CC BY 4.0',
    licenseHref: 'https://creativecommons.org/licenses/by/4.0/',
    sourceHref: 'https://commons.wikimedia.org/wiki/File:Pine_Forest_Ooty.jpg',
  },
  coonoor: {
    src: '/images/destinations/ooty/coonoor.webp',
    alt: 'Garden paths and a footbridge at Sim’s Park, Coonoor',
    author: 'PJeganathan',
    license: 'CC BY-SA 4.0',
    licenseHref: 'https://creativecommons.org/licenses/by-sa/4.0/',
    sourceHref: 'https://commons.wikimedia.org/wiki/File:Sim%27s_Park,_Coonoor_JEG9187.jpg',
  },
};

export const OOTY_PHOTO_CREDITS = Object.values(OOTY_DESTINATION_IMAGES);

/**
 * Local Ooty sightseeing cabs. Prices and stops come from the published
 * package pages. Stay, meals, and entry tickets are not included.
 * These fares do not replace the Coimbatore to Ooty taxi fare.
 */
export const OOTY_PACKAGES = [
  {
    id: 'ooty-1-day',
    duration: '1 Day',
    title: '1 Day Ooty Sightseeing',
    image: OOTY_DESTINATION_IMAGES.botanical.src,
    alt: OOTY_DESTINATION_IMAGES.botanical.alt,
    summary:
      'A private cab for one Ooty day, with pickup and drop inside Ooty and a fixed plan from about 9 AM to 5 PM.',
    places:
      'Government Botanical Garden, Rose Garden, Doddabetta Peak, tea factory and chocolate museum, Ooty Lake, Thread Garden, Wax Museum, and Tribal Museum.',
    days: [
      {
        label: 'Day plan',
        stops: [
          '9:00 AM — Pickup from an Ooty hotel or the bus stand',
          '9:30 AM — Botanical Garden and Rose Garden',
          '11:00 AM — Doddabetta Peak',
          '12:30 PM — Tea factory and chocolate museum',
          '2:00 PM — Lunch stop, paid by you',
          '3:00 PM — Ooty Lake and boating',
          '4:00 PM — Thread Garden or Wax Museum',
          '5:00 PM — Drop back in Ooty',
        ],
      },
    ],
    prices: [
      { label: 'Starting fare', amount: fromReferenceFare(2800) },
      { label: 'Sedan', amount: fromReferenceFare(3200) },
      { label: 'SUV', amount: fromReferenceFare(3800) },
    ],
    includes: [
      'Private cab and local driver',
      'Fuel',
      'Pickup and drop within Ooty',
      'Fixed sightseeing plan, about 9 AM to 5 PM',
    ],
    excludes: ['Entry tickets', 'Meals', 'Extra kilometres beyond the plan', 'Hotel stay'],
  },
  {
    id: 'ooty-2-day',
    duration: '2 Days',
    title: '2 Day Ooty Sightseeing',
    image: OOTY_DESTINATION_IMAGES.pykara.src,
    alt: OOTY_DESTINATION_IMAGES.pykara.alt,
    summary:
      'Two sightseeing days by private cab. Day one stays in Ooty town. Day two goes toward Pykara. Each day runs about 8:30 AM to 5:30 PM.',
    places:
      'Day 1 covers the Ooty gardens, Doddabetta, the tea and chocolate stop, the lake, Thread Garden, and the Wax Museum. Day 2 covers Pine Forest, 6th Mile, 9th Mile, Pykara Falls, Pykara Lake, Kamaraj Sagar Dam, and Wenlock Downs.',
    days: [
      {
        label: 'Day 1 — Ooty town',
        stops: [
          'Government Botanical Garden',
          'Rose Garden',
          'Doddabetta Peak',
          'Tea factory and chocolate museum',
          'Ooty Lake and boating',
          'Thread Garden',
          'Wax Museum',
        ],
      },
      {
        label: 'Day 2 — Pykara side',
        stops: [
          'Pine Forest',
          '6th Mile and 9th Mile',
          'Pykara Waterfalls',
          'Pykara Lake boathouse',
          'Kamaraj Sagar Dam',
          'Wenlock Downs',
        ],
      },
    ],
    prices: [
      { label: 'Starting fare', amount: fromReferenceFare(5600) },
      { label: 'Sedan', amount: fromReferenceFare(6800) },
      { label: 'SUV', amount: fromReferenceFare(7600) },
      { label: 'Innova', amount: fromReferenceFare(8500) },
    ],
    includes: [
      'Private cab and driver for two days',
      'About 8 to 9 hours of sightseeing each day',
      'Pickup from the Ooty bus stand or railway station',
    ],
    excludes: [
      'Entry tickets',
      'Meals',
      'Hotel stay',
      'Coonoor, which is a separate add-on and is not in this plan',
    ],
  },
  {
    id: 'ooty-3-day',
    duration: '3 Days',
    title: '3 Day Ooty, Coonoor and Pykara',
    image: OOTY_DESTINATION_IMAGES.coonoor.src,
    alt: OOTY_DESTINATION_IMAGES.coonoor.alt,
    summary:
      'Three cab days covering Ooty town, Coonoor, and the Pykara circuit. The sedan fare depends on how much of that circuit you book.',
    places:
      'Ooty town on day one, Coonoor on day two, and Pykara or a slower shopping day on day three.',
    days: [
      {
        label: 'Day 1 — Ooty town',
        stops: [
          'Government Botanical Garden',
          'Ooty Lake and boating',
          'Rose Garden',
          'Doddabetta Peak',
          'Tea factory and museum',
          'Chocolate factory',
          'Thread Garden',
        ],
      },
      {
        label: 'Day 2 — Coonoor',
        stops: [
          "Sim's Park",
          "Lamb's Rock",
          "Dolphin's Nose",
          'Tea estate viewpoint',
          'Wellington golf view',
          'Coonoor market',
        ],
      },
      {
        label: 'Day 3 — Pykara or a slower day',
        stops: [
          'Pykara Waterfalls, Pykara Lake, 6th Mile, 9th Mile, Pine Forest, and Kamaraj Sagar Dam',
          'Or Upper Bazaar, local chocolate shops, and a handicraft stop, then drop in Ooty',
        ],
      },
    ],
    prices: [
      { label: 'Sedan, Ooty town', amount: fromReferenceFare(5800) },
      { label: 'Sedan, Ooty and Coonoor', amount: fromReferenceFare(6800) },
      { label: 'Sedan, Ooty, Coonoor and Pykara', amount: fromReferenceFare(7900) },
    ],
    includes: [
      'Private cab and driver',
      'Fuel, tolls, and parking',
      'Start time between 8 AM and 10 AM',
    ],
    excludes: ['Entry tickets', 'Meals', 'Hotel stay', 'Toy train ticket'],
    vehicleNote: 'SUV and larger cabs were not given a separate published fare. Ask for that fare when you enquire.',
  },
  {
    id: 'ooty-4-day',
    duration: '4 Days',
    title: '4 Day Ooty, Coonoor and Pykara',
    image: OOTY_DESTINATION_IMAGES.pine.src,
    alt: OOTY_DESTINATION_IMAGES.pine.alt,
    summary:
      'Four private-cab days, about eight hours each, across Ooty, Coonoor, and Pykara, with a slower fourth day.',
    places:
      'Ooty town, Coonoor viewpoints, the Pykara and pine-forest circuit, then Cairn Hill, Avalanche, or a shopping day.',
    days: [
      {
        label: 'Day 1 — Ooty town',
        stops: [
          'Government Botanical Garden',
          'Rose Garden',
          'Doddabetta Peak',
          'Tea factory and chocolate museum',
          'Ooty Lake boating',
          'Thread Garden, optional',
        ],
      },
      {
        label: 'Day 2 — Coonoor',
        stops: [
          "Sim's Park",
          "Lamb's Rock",
          "Dolphin's Nose",
          'Tea estate viewpoint',
          'Wellington golf view',
          'Nilgiri Mountain Railway, optional and ticketed separately',
        ],
      },
      {
        label: 'Day 3 — Pykara',
        stops: [
          'Pine Forest',
          'Kamaraj Sagar Dam',
          'Wenlock Downs',
          'Pykara Falls',
          'Pykara Lake and boating',
          '6th Mile or 9th Mile',
        ],
      },
      {
        label: 'Day 4 — Optional trails',
        stops: [
          'Cairn Hill forest walk, or',
          'Avalanche and Upper Bhavani when a permit is available, or',
          'Shopping and drop in Ooty',
        ],
      },
    ],
    prices: [
      { label: 'Sedan', amount: fromReferenceFare(8200) },
      { label: 'SUV', amount: fromReferenceFare(10400) },
    ],
    includes: [
      'Private cab and driver for four days, about 8 hours a day',
      'Fuel, driver allowance, parking, and tolls',
      'Pickup and drop from an Ooty hotel',
    ],
    excludes: ['Entry tickets', 'Food', 'Hotel stay', 'Avalanche permit and toy train ticket'],
  },
  {
    id: 'ooty-5-day',
    duration: '5 Days',
    title: '5 Day Nilgiri Sightseeing',
    image: OOTY_DESTINATION_IMAGES.doddabetta.src,
    alt: OOTY_DESTINATION_IMAGES.doddabetta.alt,
    summary:
      'Five cab days, about 9 AM to 5 PM, through Ooty town, tea-country viewpoints, Pykara, and Coonoor.',
    places:
      'Ooty gardens and museums, Doddabetta and tea viewpoints, Pykara, Coonoor, and an optional Avalanche or Emerald day.',
    days: [
      {
        label: 'Day 1 — Ooty town',
        stops: [
          'Government Botanical Garden',
          'Rose Garden',
          'Ooty Lake and boating',
          'Thread Garden',
          'Wax Museum',
          'Tribal Museum',
        ],
      },
      {
        label: 'Day 2 — Viewpoints',
        stops: [
          'Doddabetta Peak',
          'Tea factory and chocolate museum',
          'Tea estate viewpoint',
          'Tree Park',
          'Cairn Hill',
        ],
      },
      {
        label: 'Day 3 — Pykara',
        stops: [
          'Pine Forest',
          '6th Mile',
          '9th Mile',
          'Pykara Lake and boating',
          'Pykara Waterfalls',
          'Kamaraj Sagar Dam',
        ],
      },
      {
        label: 'Day 4 — Coonoor',
        stops: [
          "Sim's Park",
          "Lamb's Rock",
          "Dolphin's Nose",
          'Wellington views',
          'Coonoor tea estate drive',
        ],
      },
      {
        label: 'Day 5 — Optional sights',
        stops: [
          'Avalanche, if a forest permit is available',
          'Upper Bhavani or an Emerald Lake view',
          'Tribal research centre',
          'Shola forest, or a shopping drop',
        ],
      },
    ],
    prices: [
      { label: 'Starting fare', amount: fromReferenceFare(9900) },
      { label: 'Sedan', amount: fromReferenceFare(11900) },
      { label: 'SUV', amount: fromReferenceFare(14900) },
    ],
    includes: [
      'Private cab and local driver',
      'Fuel, tolls, and parking',
      'About 8 hours a day, 9 AM to 5 PM',
      'Pickup and drop within Ooty',
    ],
    excludes: ['Hotel stay', 'Meals', 'Entry tickets', 'Avalanche forest permit'],
  },
  {
    id: 'ooty-6-day',
    duration: '6 Days',
    title: '6 Day Nilgiri Sightseeing',
    image: OOTY_DESTINATION_IMAGES.lake.src,
    alt: OOTY_DESTINATION_IMAGES.lake.alt,
    summary:
      'Six sightseeing days by private cab, about 9:30 AM to 5:30 PM, across Ooty, Coonoor, Pykara, Avalanche, and Emerald.',
    places:
      'Ooty town, Doddabetta and the pine forest, Coonoor, Pykara, Avalanche and Emerald, then museums and a final drop.',
    days: [
      {
        label: 'Day 1 — Ooty town',
        stops: [
          'Government Botanical Garden',
          'Rose Garden',
          'Ooty Lake boating',
          'Thread Garden',
          'Chocolate museum',
          'Local shopping',
        ],
      },
      {
        label: 'Day 2 — Peaks and pines',
        stops: [
          'Doddabetta Peak',
          'Tea factory and tea museum',
          'Tea estate viewpoint',
          'Ketti Valley view',
          'Pine Forest',
          '6th Mile or 9th Mile',
        ],
      },
      {
        label: 'Day 3 — Coonoor',
        stops: [
          'Toy train, optional',
          "Sim's Park",
          "Lamb's Rock",
          "Dolphin's Nose",
          'Wellington golf view',
          'Coonoor market',
        ],
      },
      {
        label: 'Day 4 — Pykara',
        stops: [
          'Kamaraj Sagar Dam',
          'Pykara Lake boating',
          'Pykara Waterfalls',
          'Shooting point',
          'Pykara village drive',
        ],
      },
      {
        label: 'Day 5 — Avalanche and Emerald',
        stops: [
          'Avalanche Lake',
          'Upper Bhavani, if a forest permit is available',
          'Emerald Lake',
          'Silent Valley view',
          'Cauliflower Shola, as an optional walk',
        ],
      },
      {
        label: 'Day 6 — Last sights',
        stops: [
          'Tribal Museum',
          'Tree Park',
          'Wenlock Downs',
          'Needle Rock viewpoint',
          'Shopping or drop',
        ],
      },
    ],
    prices: [{ label: 'Starting fare', amount: fromReferenceFare(9999) }],
    includes: [
      'Private cab and driver',
      'Pickup in Ooty town, at the railway station, or at the bus stop',
      'About 8 hours a day, 9:30 AM to 5:30 PM',
    ],
    excludes: ['Entry tickets', 'Meals', 'Hotel stay'],
    vehicleNote: 'A single starting fare was published. Sedan, SUV, and larger-cab fares were not listed separately, so those are confirmed on enquiry.',
  },
];

/** Destination cards already on this page. Not included in the taxi fare. */
export const OOTY_SIGHTSEEING_GROUPS = [
  {
    id: 'ooty-town',
    title: 'Ooty town attractions',
    places: [
      {
        id: 'botanical-garden',
        name: 'Government Botanical Garden',
        image: OOTY_DESTINATION_IMAGES.botanical.src,
        alt: OOTY_DESTINATION_IMAGES.botanical.alt,
        description:
          'Lawns, flower beds, and shaded walks near the town centre. It is usually the first garden stop on the Ooty sightseeing day.',
      },
      {
        id: 'rose-garden',
        name: 'Government Rose Garden',
        image: OOTY_DESTINATION_IMAGES.rose.src,
        alt: OOTY_DESTINATION_IMAGES.rose.alt,
        description:
          'Terraced rose beds beside the botanical garden. It pairs with that garden on the same morning.',
      },
      {
        id: 'doddabetta',
        name: 'Doddabetta Peak',
        image: OOTY_DESTINATION_IMAGES.doddabetta.src,
        alt: OOTY_DESTINATION_IMAGES.doddabetta.alt,
        description:
          'The high viewpoint above Ooty town. Mist is common, so the cab plan leaves time for the climb and the lookout.',
      },
      {
        id: 'ooty-lake',
        name: 'Ooty Lake',
        image: OOTY_DESTINATION_IMAGES.lake.src,
        alt: OOTY_DESTINATION_IMAGES.lake.alt,
        description:
          'A short lakeside stop for a walk or a boat ride. Boating is paid at the lake and is not part of the cab fare.',
      },
    ],
  },
  {
    id: 'pykara',
    title: 'Pykara sightseeing',
    places: [
      {
        id: 'pykara',
        name: 'Pykara Lake and Falls',
        image: OOTY_DESTINATION_IMAGES.pykara.src,
        alt: OOTY_DESTINATION_IMAGES.pykara.alt,
        description:
          'A full outing west of town, usually kept as its own day with the dam, the falls, and the lake boathouse.',
      },
    ],
  },
  {
    id: 'coonoor',
    title: 'Coonoor sightseeing',
    places: [
      {
        id: 'coonoor',
        name: 'Coonoor',
        image: OOTY_DESTINATION_IMAGES.coonoor.src,
        alt: OOTY_DESTINATION_IMAGES.coonoor.alt,
        description:
          'The neighbouring hill town on the longer cab plans, including Sim’s Park, Lamb’s Rock, and Dolphin’s Nose. The toy train is optional and ticketed separately.',
      },
    ],
  },
  {
    id: 'nilgiri-nature',
    title: 'Nilgiri nature destinations',
    places: [
      {
        id: 'pine-forest',
        name: 'Pine Forest',
        image: OOTY_DESTINATION_IMAGES.pine.src,
        alt: OOTY_DESTINATION_IMAGES.pine.alt,
        description:
          'The pine stretch on the way toward the Pykara shooting spots. It is part of the Pykara day, not a separate taxi drop.',
      },
    ],
  },
];

/** @deprecated Flat list kept so older imports still resolve. */
export const OOTY_SIGHTSEEING = OOTY_SIGHTSEEING_GROUPS.flatMap((group) => group.places);

export const CBE_OOTY_FAQS = [
  {
    question: 'How do I book a taxi from Coimbatore to Ooty?',
    answer:
      'Use the booking card on this page. Pickup starts as Coimbatore and drop as Ooty, and both can be edited. Enter your mobile number, choose Get Fare Estimate, then pick Sedan, SUV, or Crysta with your date and time. The request uses the same EasyDropTaxi booking form as the homepage.',
  },
  {
    question: 'Can I book a one-way taxi?',
    answer:
      'Yes. One Way is the usual choice when you will stay in Ooty and do not need the same cab to wait. The one-way fare is Sedan ₹2,699, SUV ₹3,500, and Crysta ₹4,300, and it is the same from Ooty back to Coimbatore.',
  },
  {
    question: 'Can I book a round-trip taxi?',
    answer:
      'Yes. Choose Round Trip in the booking card. Return dates appear in the form, and the round-trip estimate uses the existing EasyDropTaxi fare calculation rather than the fixed one-way amounts.',
  },
  {
    question: 'Are tolls and parking included in the taxi fare?',
    answer:
      'Yes for the fixed one-way Coimbatore to Ooty fare, and for the return one-way fare. Sightseeing entry tickets, meals, and hotel stays are separate.',
  },
  {
    question: 'Which vehicles can I book?',
    answer:
      'The taxi form offers Sedan, SUV, and Crysta. Sightseeing cab cards also show a sedan or SUV fare where that fare was published. If a vehicle fare is not listed, the enquiry confirms it.',
  },
  {
    question: 'What do the Ooty sightseeing packages include?',
    answer:
      'They are private sightseeing cabs that start and end in Ooty. Each card lists the days, the places, the inclusions, and the exclusions. Hotel rooms, meals, and attraction tickets are not included.',
  },
  {
    question: 'How long are the sightseeing plans?',
    answer:
      'Plans run from 1 to 6 days. A day is a cab sightseeing day of about 8 hours. It is not a hotel package.',
  },
  {
    question: 'How is a sightseeing package priced?',
    answer:
      'Each card shows the cab fare for that plan. The Coimbatore to Ooty taxi fare is not included in those amounts. Use Enquire on the card, or call or WhatsApp, and the team confirms the vehicle and the date.',
  },
  {
    question: 'Does the taxi booking form reserve a sightseeing package?',
    answer:
      'No. Get Fare Estimate is for the Coimbatore to Ooty taxi. Sightseeing plans are requested with the Enquire button, a call, or WhatsApp.',
  },
  {
    question: 'Can the sightseeing order change?',
    answer:
      'The published plans follow a set route so the day stays practical. Small changes can be requested when you enquire. Avalanche and the toy train depend on permits or tickets and are not guaranteed.',
  },
];

export const CBE_OOTY_WHATSAPP_MESSAGE =
  'Hello, I want a Coimbatore to Ooty taxi. Please help with fare and availability.';
